//
// Copyright (c) 2025 Comcast
//

import { useTranslation } from 'react-i18next'
import { useState, useEffect, useContext, useCallback } from 'react'
import { useNavigate, Link } from 'react-router'
import { useSelector } from 'react-redux'

import {
  useLocationQueryParams,
  useSetSearchParamsWithQueryString,
} from 'utils/hooks'

import { useSortableColumnsOptions } from 'components/Shared/SortDropdown/hooks'

import styled from 'styled-components'

import {
  useFetchTicketsQuery,
  useRefetchTicketsMutation,
} from 'api/tickets'

import {
  useDeleteTicketProviderTicketsMutation,
} from 'api/ticketProviderTickets'

import {
  useFetchTicketFiltersQuery,
} from 'api/ticketFilters'

import * as sessionUserSelector from 'selectors/sessionUser'

import { AbilityContext } from 'ability-context'
import {
  RouteTicketingSystems,
  RouteTicketingSystemsByPartner,
  RouteTicketingSystemsTickets,
  RouteTicketingSystemsTicketNew,
  RouteSubscriptionsViews,
} from 'routes'

import colors from 'components/utils/colors'
import { capitalizeFirstLetter } from 'utils/strings'

import { confirm } from 'components/Shared/ConfirmDialog/Index'
import ErrorPage404 from 'components/App/ErrorPage/ErrorPage404'
import MainWrap from 'components/Shared/Skeleton/MainWrap/Index'
import Content from 'components/Shared/Skeleton/Content/Index'
import ScrollableTabsPanel from 'components/App/ScrollableTabsPanel/Index'
import Header from 'components/Shared/Header/Index'
import ActionButtons from 'components/Shared/Skeleton/ActionButtons/Index'
import FilterPanel from 'components/Shared/Skeleton/FilterPanel/Index'
import FilterPanelTitle from 'components/Shared/Skeleton/FilterPanel/FilterPanelTitle'
import UnwrappableSection from 'components/Shared/Skeleton/FilterPanel/UnwrappableSection'
import FilterPanelSearch from 'components/Shared/Skeleton/FilterPanel/FilterPanelSearch'
import SortDropdown from 'components/Shared/SortDropdown/Index'
import IconLink from 'components/Shared/IconLink/Index'
import ClearFiltersIcon from 'components/Shared/ClearFiltersIcon/Index'
import ExportCsv from 'components/Shared/ExportCsv/Index'
import Tooltip from 'components/Shared/Tooltip/Index'
import TicketsTable from './shared/TicketsTable/Index'

import FiltersSidebar from './FiltersSidebar/Index'
import TicketSidebar from './shared/TicketSidebar'
import SubscriptionToNewTickets from './SubscriptionToNewTickets'
import CardsListPanel from 'components/Shared/CardsListPanel/Index'
import MixedFiltersField from 'components/Shared/MixedFiltersField/Index'
import RowsCountAndPagination from 'components/Shared/RowsCountAndPagination/Index'

import { useCurrentTicketingSystem } from './shared/hooks'

const SubscriptionButtonsAndReset = styled.div`
  display: flex;
  gap: 4px;
  align-items: center;
`
const StyledActionBarLinks = styled.div`
  display: flex;
  align-items: center;
  height: 24px;
`

const TicketsPage = () => {
  const [selectedTicket, setSelectedTicket] = useState(null)
  const [bulkDelete, setBulkDelete] = useState(false)
  const [checkedTicketIds, setCheckedTicketIds] = useState([])
  const [checkedTicketExternalIds, setCheckedTicketExternalIds] = useState([])
  const [isSubscribingOrUnsubscribing, setIsSubscribingOrUnsubscribing] = useState(false)
  const [isTicketFilterRemoving, setIsTicketFilterRemoving] = useState(false)
  const [needRemoveCsvFile, setNeedRemoveCsvFile] = useState(false)
  const [isFiltersSidebarVisible, setIsFiltersSidebarVisible] = useState(false)

  const { t } = useTranslation()
  const [locationQueryParams] = useLocationQueryParams()
  const setQueryParam = useSetSearchParamsWithQueryString()
  const navigate = useNavigate()
  const ability = useContext(AbilityContext)
  const isUserPrivileged = useSelector(sessionUserSelector.isUserPrivileged)

  const activePage = parseInt(locationQueryParams.page) || 1
  const searchValue = locationQueryParams.search || ''
  const statusFilters = locationQueryParams.status_filters || []
  const currentTicketingSystem = useCurrentTicketingSystem()
  const selectedProviderTicketBoardId =
    locationQueryParams.provider_ticket_board_id || currentTicketingSystem?.providerTicketBoards[0]?.id

  const providerTicketBoards = currentTicketingSystem?.providerTicketBoards || []

  const selectedProviderTicketBoard = providerTicketBoards.find(board => board.id === selectedProviderTicketBoardId)

  const defaultSortField = selectedProviderTicketBoard?.defaultSortField
  const defaultSortOrder = selectedProviderTicketBoard?.defaultSortOrder

  let sortField = locationQueryParams.sort_field || defaultSortField
  let sortDir = locationQueryParams.sort_dir || defaultSortOrder

  // user_filters are kept for backward compatibility of links. May be removed later.
  const activeTicketFilterIds = locationQueryParams.ticket_filters || locationQueryParams.user_filters || []

  const ticketingSystemDisplayId = currentTicketingSystem?.displayId
  const ticketingSystemName = currentTicketingSystem?.name
  const ticketBonding = currentTicketingSystem?.ticketBonding

  const filterParams = {
    'search': searchValue,
    'page': activePage,
    'statusFilters': statusFilters,
    'ticketingSystemDisplayId': ticketingSystemDisplayId,
    'userFilters': activeTicketFilterIds,
    'providerTicketBoardId': selectedProviderTicketBoardId,
  }

  const { data, isFetching } = useFetchTicketsQuery({
    ticketingSystemDisplayId: ticketingSystemDisplayId,
    page: activePage,
    searchValue: searchValue,
    statusFilters: statusFilters,
    userFilters: activeTicketFilterIds,
    sortField: sortField,
    sortDir: sortDir,
    providerTicketBoardId: selectedProviderTicketBoardId,
  })

  const exportingRecordId = data?.exportingRecordId

  const { data: ticketFiltersData, isFetching: isTicketFiltersFetching } = useFetchTicketFiltersQuery({
    ticketingSystemDisplayId: ticketingSystemDisplayId,
    providerTicketBoardId: selectedProviderTicketBoardId,
  })

  const [refetchTickets, { isLoading: isRefetchingTickets }] = useRefetchTicketsMutation()
  const [deleteProviderTickets, { isLoading: isDeletingProviderTickets }] = useDeleteTicketProviderTicketsMutation()

  // clear selected ticket if it is absent in fetched tickets
  useEffect(() => {
    if (isFetching) {
      return
    }

    const hasSelectedTicket = selectedTicket && data.tickets.some(t => t.id === selectedTicket.id)
    if (!hasSelectedTicket) {
      setSelectedTicket(null)
    }
  }, [isFetching]) // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    setBulkDelete(false)
  }, [ticketingSystemDisplayId])

  const csvExportType = 'ticketing_system_tickets'

  const tickets = data?.tickets || []
  const partnerStatuses = data?.partnerStatuses || []
  const subscribed = data?.subscribed || false
  const ticketsCount = data?.ticketsCount || 0
  const totalPages = data?.totalPages || 1
  const ticketsIds = tickets.map(t => t.id)
  const ticketsExternalIds = tickets.map(t => t.externalId)
  const relatedSystems = data?.metaData?.relatedSystems || null
  const partnerId = data?.partnerId || null
  const partnerName = capitalizeFirstLetter(data?.partnerName || '')
  const sortableColumns = data?.sortableColumns || []

  // Disable reload tickets for ama_kinesis_case provider
  // due to different data in kinesis stream and AMA GET response payload
  const reloadTicketsAvailable = data?.reloadTickets

  if (!sortableColumns.includes(sortField) && sortField !== 'id') {
    sortField = 'id'
  }

  const sortDirs = ['asc', 'desc']

  if (!sortDirs.includes(sortDir)) {
    sortDir = 'desc'
  }

  const additionalSortableColumnsParams = [{ value: 'id', label: t('ticketsPage.sortRelayTicketIdLabel').toUpperCase() }]
  const sortableColumnsOptions = useSortableColumnsOptions(sortableColumns, additionalSortableColumnsParams)

  const ticketFilters = ticketFiltersData?.filters || []

  const getBreadcrumbs = () => {
    let ticketingSystemsUrl = RouteTicketingSystems()
    let ticketingSystems = t('common.breadcrumbTicketingSystems')
    if (isUserPrivileged) {
      ticketingSystems = t('common.breadcrumbTicketingSystemsByPartner', { partnerName: partnerName })
      ticketingSystemsUrl = RouteTicketingSystemsByPartner({ partnerId: partnerId })
    }

    const breadcrumbsList = [{
      text: ticketingSystems,
      url: ticketingSystemsUrl,
    }, {
      text: ticketingSystemName,
    }]
    return breadcrumbsList
  }
  const onTicketSelected = (ticket) => {
    setSelectedTicket(ticket)
  }

  const onCsvRemoving = useCallback(() => {
    setNeedRemoveCsvFile(false)
  }, [])

  const onTicketFilterRemoving = useCallback((value) => {
    setIsTicketFilterRemoving(value)
  }, [])

  const onSubscribingOrUnsubscribing = useCallback((value) => {
    setIsSubscribingOrUnsubscribing(value)
  }, [])

  const toggleFiltersSidebar = () => {
    setIsFiltersSidebarVisible(!isFiltersSidebarVisible)
  }

  const showFiltersSidebar = () => {
    setIsFiltersSidebarVisible(true)
  }

  const hideFiltersSidebar = () => {
    setIsFiltersSidebarVisible(false)
  }

  const onSidebarClose = () => {
    setSelectedTicket(null)
  }

  const getDefaultUrlParams = () => {
    const params = {
      page: 1,
      search: searchValue,
      status_filters: statusFilters,
      ticket_filters: activeTicketFilterIds,
      sort_field: defaultSortField,
      sort_dir: defaultSortOrder,
      provider_ticket_board_id: selectedProviderTicketBoardId,
    }

    return params
  }

  const updateURL = (params) => {
    const newParams = {
      ...getDefaultUrlParams(),
      ...params,
    }

    setQueryParam(newParams)
  }

  const onSearch = (newSearchValue) => {
    setNeedRemoveCsvFile(true)
    updateURL({
      search: newSearchValue,
    })
  }

  // newFilters = { ticket_filters: [...], status_filters: [...] }
  const onFilterChange = (newFilters) => {
    setNeedRemoveCsvFile(true)
    updateURL(newFilters)
  }

  const onFilterClear = () => {
    onFilterChange({ ticket_filters: [], status_filters: [] })
  }

  const reloadTickets = () => {
    refetchTickets({ ticketingSystemDisplayId: ticketingSystemDisplayId }).then(fulfilled => {
      if (!fulfilled.data?.error) {
        navigate({
          pathname: RouteTicketingSystemsTickets({ ticketingSystemDisplayId: ticketingSystemDisplayId }),
        })
      }
    })
  }

  const onSortFieldChange = ({ sortField, sortDir }) => {
    updateURL({
      sort_field: sortField,
      sort_dir: sortDir,
    })
  }

  const onToggleBulkDelete = () => {
    setCheckedTicketIds([])
    setCheckedTicketExternalIds([])
    setBulkDelete(!bulkDelete)
  }

  const doDeleteTickets = () => {
    const ticketProviderId = data?.metaData?.relatedSystems?.ticketProvider?.id
    const checkedTickets = data?.tickets.filter(t => checkedTicketIds.includes(t.id))
    const checkedProviderTicketIds = checkedTickets.map(t => t.providerTicketId)

    return deleteProviderTickets({
      ticketProviderId: ticketProviderId,
      ticketIds: checkedProviderTicketIds,
    })
  }

  const onDeleteCheckedTickets = () => {
    const title = t('ticketsPage.removeTicketsTitle')
    const message = t('ticketsPage.removeTicketsMessage',
      {
        count: checkedTicketExternalIds.length,
        numbers: checkedTicketExternalIds.join(', '),
      }
    )

    confirm({
      title: title,
      message: message,
    }).then(
      () => {
        doDeleteTickets().then(fulfilled => {
          setBulkDelete(false)
          setCheckedTicketIds([])
          setCheckedTicketExternalIds([])
          if (!fulfilled.data?.error) {
            navigate({
              pathname: RouteTicketingSystemsTickets({ ticketingSystemDisplayId: ticketingSystemDisplayId }),
            })
          }
        })
      },
      () => {}
    )
  }

  // if all tickets on the current page are checked, uncheck them
  // otherwise check all tickets on the current page
  // keep checked tickets from other pages
  const onCheckAllRowsToggle = () => {
    const checkedTicketsIdsOnPage = checkedTicketIds.filter(id => ticketsIds.includes(id))
    const checkedTicketsExternalIdsOnPage = checkedTicketExternalIds.filter(externalId => ticketsExternalIds.includes(externalId))
    if (ticketsIds.length === checkedTicketsIdsOnPage.length) {
      const newTicketsIds = checkedTicketIds.filter(id => !checkedTicketsIdsOnPage.includes(id))
      setCheckedTicketIds(newTicketsIds)

      const newTicketsExternalIds = checkedTicketExternalIds.filter(id => !checkedTicketsExternalIdsOnPage.includes(id))
      setCheckedTicketExternalIds(newTicketsExternalIds)
    } else {
      const checkedIdsAtOtherPages = checkedTicketIds.filter(id => !ticketsIds.includes(id))
      setCheckedTicketIds([...checkedIdsAtOtherPages, ...ticketsIds])

      const checkedExternalIdsAtOtherPages = checkedTicketExternalIds.filter(externalId => !ticketsExternalIds.includes(externalId))
      setCheckedTicketExternalIds([...checkedExternalIdsAtOtherPages, ...ticketsExternalIds])
    }
  }

  const onRowCheck = (ticketId, ticketExternalId) => {
    if (checkedTicketIds.includes(ticketId)) {
      const newTicketsIds = checkedTicketIds.filter(id => id !== ticketId)
      setCheckedTicketIds(newTicketsIds)

      const newTicketsExternalIds = checkedTicketExternalIds.filter(id => id !== ticketExternalId)
      setCheckedTicketExternalIds(newTicketsExternalIds)
    } else {
      setCheckedTicketIds([...checkedTicketIds, ticketId])

      setCheckedTicketExternalIds([...checkedTicketExternalIds, ticketExternalId])
    }
  }

  const onManageSubscripions = () => {
    navigate(RouteSubscriptionsViews({ ticketingSystemDisplayId: ticketingSystemDisplayId }))
  }

  const onTicketBoardSelect = (ticketBoard) => {
    const params = {
      provider_ticket_board_id: ticketBoard.id,
      sort_field: ticketBoard.defaultSortField,
      sort_dir: ticketBoard.defaultSortOrder,
      ticket_filters: [],
      status_filters: [],
    }

    updateURL(params)
  }

  const buildSelectedFilterList = () => {
    const result = []

    activeTicketFilterIds.forEach((filterId) => {
      const filter = ticketFilters.find(filter => filter.id === filterId)

      if (filter) {
        const otherFilterIds = activeTicketFilterIds.filter(id => id !== filter.id)
        let color = colors.pausedGray

        if (filter.global) {
          color = filter.published ? colors.globalGray : colors.infoGray
        }

        result.push({
          value: filter.name,
          color: color,
          onClear: () => onFilterChange({ ticket_filters: otherFilterIds }),
        })
      }
    })

    return result
  }

  const buildSelectedMultipleFilterList = () => {
    if (!statusFilters?.length) {
      return []
    }

    const items = []

    statusFilters.forEach((statusName) => {
      const status = partnerStatuses.find(s => s.name === statusName)

      if (status) {
        items.push({ value: status.name, bgColor: status.color })
      }
    })

    return [{
      title: t('ticketsPage.filtersStatusSectionTitle'),
      items: items,
      clearTooltip: t('ticketsPage.filterPanelClearStatusesTooltip'),
      onClear: () => onFilterChange({ status_filters: [] }),
    }]
  }

  if (!ticketingSystemDisplayId) {
    return <ErrorPage404 />
  }

  const clearFiltersUrl =
    `${RouteTicketingSystemsTickets({ ticketingSystemDisplayId })}?provider_ticket_board_id=${selectedProviderTicketBoardId}`

  const canDeleteTickets = ability.can('delete', 'Ticket')
  const canReloadTickets = ability.can('reload', 'Tickets') && reloadTicketsAvailable
  const isAnyActiveFilters = statusFilters.length > 0 || activeTicketFilterIds.length > 0
  const isAnyActiveFiltersOrSearch = searchValue || isAnyActiveFilters
  const isLoading = isFetching || isRefetchingTickets ||
    isDeletingProviderTickets || isSubscribingOrUnsubscribing ||
    isTicketFiltersFetching || isTicketFilterRemoving

  const showNewLink = currentTicketingSystem.canCreateTicket
  const fixedHeader = isUserPrivileged && (
    <ScrollableTabsPanel
      relatedSystems={relatedSystems}
      ticketingSystemDisplayId={ticketingSystemDisplayId}
    />
  )

  const showActionBarLinks = currentTicketingSystem.actionBarLinks.length > 0

  return (
    <MainWrap
      pageTitle={ticketingSystemName}
      fixedHeader={fixedHeader}
    >
      <FiltersSidebar
        isVisible={isFiltersSidebarVisible}
        onClose={hideFiltersSidebar}
        ticketingSystemDisplayId={ticketingSystemDisplayId}
        activeTicketFilterIds={activeTicketFilterIds}
        ticketFilters={ticketFilters}
        onFilterChange={onFilterChange}
        onTicketFilterRemoving={onTicketFilterRemoving}
        isTicketFiltersFetching={isTicketFiltersFetching}
        partnerStatuses={partnerStatuses}
        activeStatusFilters={statusFilters}
        providerTicketBoards={providerTicketBoards}
        selectedProviderTicketBoardId={selectedProviderTicketBoardId}
      />

      <TicketSidebar
        ticket={selectedTicket}
        onClose={onSidebarClose}
        searchValue={searchValue}
      />
      <Content loading={isLoading}>
        <Header
          title={ticketingSystemName}
          breadcrumbsList={getBreadcrumbs()}
        />
        <ActionButtons>
          <>
            {canReloadTickets && (
              <IconLink
                onClick={reloadTickets}
                iconName='icon-refetch'
                text={t('ticketsPage.reloadTicketsLink')}
              />
            )}
            {canDeleteTickets && (
              <>
                <IconLink
                  active={bulkDelete}
                  onClick={onToggleBulkDelete}
                  iconName='icon-remove'
                  text={t('ticketsPage.bulkRemoveLink')}
                />
                {bulkDelete && checkedTicketIds.length > 0 && (
                  <IconLink
                    onClick={onDeleteCheckedTickets}
                    iconName='icon-delete-list'
                    text={t('ticketsPage.removeTicketsLink')}
                  />
                )}
              </>
            )}
          </>
          {showNewLink && (
            <Link to={RouteTicketingSystemsTicketNew({ ticketingSystemDisplayId })}>
              <IconLink
                iconName='icon-plus'
                text={t('mainHeader.partnersNewTicket')}
              />
            </Link>
          )}
          <ExportCsv
            type={csvExportType}
            recordCount={ticketsCount}
            exportingRecordId={exportingRecordId}
            isFetching={isFetching}
            filterParams={filterParams}
            needRemoveCsvFile={needRemoveCsvFile}
            onCsvRemoving={onCsvRemoving}
          />
          {showActionBarLinks && (
            currentTicketingSystem.actionBarLinks.map((link, index) => (
              <StyledActionBarLinks key={index}>
                <Link
                  to={link.url}
                  target='_blank'
                >
                  <IconLink
                    iconName='icon-status-bonded-24'
                    text={link.text}
                  />
                </Link>
              </StyledActionBarLinks>
            ))
          )}
        </ActionButtons>

        {(providerTicketBoards.length > 1) && (
          <CardsListPanel
            onSelect={onTicketBoardSelect}
            cards={providerTicketBoards}
            selectedCardId={selectedProviderTicketBoardId}
            noPadding
            noUpperStyle
          />
        )}

        <FilterPanel dataTest='tickets-page-filter-panel'>
          <FilterPanelTitle
            title={t('ticketsPage.filterPanelTitle')}
            tooltipText={t('ticketsPage.filterPanelTitleTooltip')}
            onClick={toggleFiltersSidebar}
          />

          <MixedFiltersField
            filterList={buildSelectedFilterList()}
            multipleFilterList={buildSelectedMultipleFilterList()}
            maxDisplayItems={3}
            onClear={onFilterClear}
            onClearTooltip={t('ticketsPage.filterPanelClearFiltersTooltip')}
            tooltipText={t('ticketsPage.filterPanelFiltersTooltip')}
            tooltipId='tooltip-filter-panel'
            onContractorClick={showFiltersSidebar}
          />

          <UnwrappableSection>
            <FilterPanelSearch
              key={searchValue}
              initialValue={searchValue}
              onChange={onSearch}
              placeholder={t('ticketsPage.searchPlaceholderNew')}
              buttonTooltip={t('ticketsPage.filterPanelSearchTooltip')}
              tooltipId='tooltip-filter-panel'
            />
            {(!isUserPrivileged || isAnyActiveFiltersOrSearch) && (
              <SubscriptionButtonsAndReset isEmpty={isUserPrivileged && !isAnyActiveFiltersOrSearch}>
                {!isUserPrivileged && (
                  <>
                    <SubscriptionToNewTickets
                      initialSubscribed={subscribed}
                      onSubscribingOrUnsubscribing={onSubscribingOrUnsubscribing}
                      providerTicketBoardId={selectedProviderTicketBoardId}
                    />
                    <IconLink
                      data-tooltip-id='subscribe-tooltip-id'
                      onClick={onManageSubscripions}
                      iconName='icon-manage-subscription-16'
                      data-tooltip-content={t('ticketsPage.manageSubscriptions')}
                    />
                  </>
                )}

                {isAnyActiveFiltersOrSearch && (
                  <ClearFiltersIcon
                    clearUrl={clearFiltersUrl}
                    tooltipText={t('ticketsPage.clearFiltersLink')}
                  />
                )}
              </SubscriptionButtonsAndReset>
            )}
            <div />
          </UnwrappableSection>

          <SortDropdown
            title={t('common.sortedBy')}
            onChange={onSortFieldChange}
            value={{ sortField: sortField, sortDir: sortDir }}
            options={sortableColumnsOptions}
            defaultSortField={defaultSortField}
            defaultSortDir={defaultSortOrder}
            clearActionTooltip={t('ticketsPage.filterPanelClearSortingTooltip')}
          />
          <Tooltip id='tooltip-filter-panel' />
        </FilterPanel>

        <TicketsTable
          tickets={tickets}
          ticketingSystem={currentTicketingSystem}
          selectedTicketId={selectedTicket?.id}
          onRowClick={onTicketSelected}
          partnerStatusesMap={data?.partnerStatusesMap || {}}
          searchValue={searchValue}
          checkableRows={bulkDelete}
          onCheckAllRowsToggle={onCheckAllRowsToggle}
          checkedTicketIds={checkedTicketIds}
          onRowCheck={onRowCheck}
          ticketBonding={ticketBonding}
          providerTicketBoard={selectedProviderTicketBoard}
        />

        <RowsCountAndPagination
          countText={t('ticketsPage.ticketsCount', { count: ticketsCount })}
          activePage={activePage}
          totalPages={totalPages}
          isFetching={isFetching}
          dataTest='tickets-count'
        />
      </Content>
    </MainWrap>
  )
}

export default TicketsPage
